import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Build an absolute URL for admin-managed media (relative /api/media/... paths)
export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const http = axios.create({ baseURL: BASE });
http.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("sl_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const norm = (c) => ({ ...c, from: c.from_ ?? c.from ?? "" });
const denorm = (c) => { const { from, ...rest } = c; return { ...rest, from_: from ?? c.from_ ?? "" }; };

export const api = {
  // auth
  register: (data) => http.post("/auth/register", data).then((r) => r.data),
  login: (data) => http.post("/auth/login", data).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),

  // settings
  getSettings: () => http.get("/settings").then((r) => r.data),
  saveSettings: (s) => http.put("/settings", s).then((r) => r.data),

  // site images (admin-managed)
  getSiteImages: () => http.get("/site-images").then((r) => r.data),
  uploadSiteImage: (key, file) => {
    const fd = new FormData();
    fd.append("key", key);
    fd.append("file", file);
    return http.post("/site-images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  resetSiteImage: (key) => http.delete(`/site-images/${key}`).then((r) => r.data),

  // corridors
  getCorridors: () => http.get("/corridors").then((r) => r.data.map(norm)),
  addCorridor: (c) => http.post("/corridors", denorm(c)).then((r) => norm(r.data)),
  updateCorridor: (id, c) => http.put(`/corridors/${id}`, denorm(c)).then((r) => norm(r.data)),
  deleteCorridor: (id) => http.delete(`/corridors/${id}`).then((r) => r.data),

  // services
  getServices: () => http.get("/services").then((r) => r.data),
  addService: (s) => http.post("/services", s).then((r) => r.data),
  updateService: (id, s) => http.put(`/services/${id}`, s).then((r) => r.data),
  deleteService: (id) => http.delete(`/services/${id}`).then((r) => r.data),

  // faqs
  getFaqs: () => http.get("/faqs").then((r) => r.data),
  addFaq: (f) => http.post("/faqs", f).then((r) => r.data),
  updateFaq: (id, f) => http.put(`/faqs/${id}`, f).then((r) => r.data),
  deleteFaq: (id) => http.delete(`/faqs/${id}`).then((r) => r.data),

  // countries
  getCountries: () => http.get("/countries").then((r) => r.data),
  addCountry: (c) => http.post("/countries", c).then((r) => r.data),
  deleteCountry: (id) => http.delete(`/countries/${id}`).then((r) => r.data),

  // warehouses
  getWarehouses: () => http.get("/warehouses").then((r) => r.data),
  addWarehouse: (w) => http.post("/warehouses", w).then((r) => r.data),
  updateWarehouse: (id, w) => http.put(`/warehouses/${id}`, w).then((r) => r.data),
  deleteWarehouse: (id) => http.delete(`/warehouses/${id}`).then((r) => r.data),

  // offers
  getOffers: () => http.get("/offers").then((r) => r.data),
  addOffer: (o) => http.post("/offers", o).then((r) => r.data),
  updateOffer: (id, o) => http.put(`/offers/${id}`, o).then((r) => r.data),
  deleteOffer: (id) => http.delete(`/offers/${id}`).then((r) => r.data),

  // insurance
  getInsurance: () => http.get("/insurance").then((r) => r.data),
  addInsurance: (o) => http.post("/insurance", o).then((r) => r.data),
  updateInsurance: (id, o) => http.put(`/insurance/${id}`, o).then((r) => r.data),
  deleteInsurance: (id) => http.delete(`/insurance/${id}`).then((r) => r.data),

  // contacts (customer-service numbers)
  getContacts: () => http.get("/contacts").then((r) => r.data),
  addContact: (o) => http.post("/contacts", o).then((r) => r.data),
  updateContact: (id, o) => http.put(`/contacts/${id}`, o).then((r) => r.data),
  deleteContact: (id) => http.delete(`/contacts/${id}`).then((r) => r.data),

  // reviews
  getReviews: () => http.get("/reviews").then((r) => r.data),
  addReview: (rv) => http.post("/reviews", rv).then((r) => r.data),

  // submissions
  addSubmission: (s) => http.post("/submissions", s).then((r) => r.data),
  getSubmissions: () => http.get("/submissions").then((r) => r.data),
  mySubmissions: () => http.get("/submissions/mine").then((r) => r.data),
  updateSubmission: (id, patch) => http.put(`/submissions/${id}`, patch).then((r) => r.data),
  addSubmissionNote: (id, text) => http.post(`/submissions/${id}/notes`, { text }).then((r) => r.data),
  deleteSubmission: (id) => http.delete(`/submissions/${id}`).then((r) => r.data),

  // activity log (admin)
  getActivity: () => http.get("/activity").then((r) => r.data),

  // tracking
  track: (data) => http.post("/track", data).then((r) => r.data),
  getTracking: () => http.get("/tracking").then((r) => r.data),
  updateTracking: (id, patch) => http.put(`/tracking/${id}`, patch).then((r) => r.data),
  addTrackingNote: (id, text) => http.post(`/tracking/${id}/notes`, { text }).then((r) => r.data),
  deleteTracking: (id) => http.delete(`/tracking/${id}`).then((r) => r.data),

  // employees
  addInquiry: (data) => http.post("/inquiries", data).then((r) => r.data),
  getInquiries: () => http.get("/inquiries").then((r) => r.data),
  updateInquiry: (id, status) => http.patch(`/inquiries/${id}`, { status }).then((r) => r.data),
  replyInquiry: (id, text) => http.post(`/inquiries/${id}/replies`, { text }).then((r) => r.data),
  deleteInquiry: (id) => http.delete(`/inquiries/${id}`).then((r) => r.data),

  // employees
  getEmployees: () => http.get("/employees").then((r) => r.data),
  getStaffNames: () => http.get("/staff-names").then((r) => r.data),
  addEmployee: (e) => http.post("/employees", e).then((r) => r.data),
  updateEmployee: (id, e) => http.put(`/employees/${id}`, e).then((r) => r.data),
  deleteEmployee: (id) => http.delete(`/employees/${id}`).then((r) => r.data),
};

// lucide icon-name map for services
import { Home, Plane, Truck, Warehouse, ShieldCheck, PackageCheck, Globe, Ship, Box } from "lucide-react";
export const ICONS = {
  home: Home, plane: Plane, truck: Truck, warehouse: Warehouse,
  "shield-check": ShieldCheck, "package-check": PackageCheck, globe: Globe, ship: Ship, box: Box,
};
export const ICON_OPTIONS = Object.keys(ICONS);
